import React from 'react';

import RuFlagIcon from '@edna/country-flags/country-flags/RU.svg';
import {ECurrency} from '@edna/utils';

import {
  BeelineIcon,
  BellIcon,
  KTelecomIcon,
  KtkTelecomIcon,
  MaxBotCircleIcon,
  MaxCircleIcon,
  MegafonIcon,
  MotivIcon,
  MtsIcon,
  NotifyIcon,
  OkNotifyIcon,
  PushCircleIcon,
  SberIcon,
  SmsCircleIcon,
  SmsIcon,
  Tele2Icon,
  TelegramCircleIcon,
  ViberCircleIcon,
  ViberIcon,
  VkNotifyIcon,
  WhatsAppCircleIcon,
  WhatsAppIcon,
} from 'src/icons';

enum ELanguage {
  ru = 'ru',
  en = 'en',
}

type TLanguage = keyof typeof ELanguage;

enum EStorageKeys {
  NEWS = 'NEWS',
  ADVICES = 'ADVICES',
  BANNERS = 'BANNERS',
  TENANT_ID = 'TENANT_ID',
  LANG = 'LANG',
  NOTIFICATION_TIME = 'NOTIFICATION_TIME',
  USERNAME = 'USERNAME',
  MENU_OPEN = 'MENU_OPEN',
  BROADCASTS_FILTERS = 'BROADCASTS_FILTERS',
  CASCADES_FILTERS = 'CASCADES_FILTERS',
  CHANNELS_FILTERS = 'CHANNELS_FILTERS',
  CHANNELS_FILTERS_SUPPORT = 'CHANNELS_FILTERS_SUPPORT',
  LEAD_COMPANY_CLIENTS_FILTERS = 'LEAD_COMPANY_CLIENTS_FILTERS',
  AGENTS_COMPANY_CLIENTS_FILTERS = 'AGENTS_COMPANY_CLIENTS_FILTERS',
  MESSAGE_MATCHERS_FILTERS = 'MESSAGE_MATCHERS_FILTERS',
  MESSAGE_MATCHERS_FILTERS_SUPPORT = 'MESSAGE_MATCHERS_FILTERS_SUPPORT',
  MESSAGE_MATCHER_PRESETS_FILTERS = 'MESSAGE_MATCHER_PRESETS_FILTERS',
  MESSAGE_MATCHER_REPORTS_FILTERS = 'MESSAGE_MATCHER_REPORTS_FILTERS',
  SUPPORT_CLIENTS_FILTERS = 'SUPPORT_CLIENTS_FILTERS',
  SUBSCRIBERS_FILTERS = 'SUBSCRIBERS_FILTERS',
  AGENTS_CLIENTS_FILTERS = 'AGENTS_CLIENTS_FILTERS',
  AGENTS_CLIENTS_PAYMENTS_FILTERS = 'AGENTS_CLIENTS_PAYMENTS_FILTERS',
  SUPPORT_VERIFICATIONS_FILTERS = 'SUPPORT_VERIFICATIONS_FILTERS',
  TARIFFS_CATALOG = 'TARIFFS_CATALOG',
}

enum ESortDirection {
  ASC = 'ASC',
  DESC = 'DESC',
}

enum EOperatorName {
  RuMts = 'russia-mts',
  RuBeeline = 'russia-beeline',
  RuTele2 = 'russia-tele2',
  RuMegafon = 'russia-megafon',
  RuYota = 'russia-yota',
  RuVainahtelecom = 'russia-vainah_telecom',
  RuMotiv = 'russia-motiv',
  RuKtelecom = 'russia-ktelecom',
  RuKtktelecom = 'russia-ktktelecom',
  RuSbertelecom = 'russia-sbertelecom',
  RuDefault = 'russia-default', // Прочие операторы России
}

export const EmptyIcon = () => null;

export const OPERATOR_ICONS: Record<EOperatorName, TSvgComponentType> = {
  [EOperatorName.RuMts]: MtsIcon,
  [EOperatorName.RuBeeline]: BeelineIcon,
  [EOperatorName.RuTele2]: Tele2Icon,
  [EOperatorName.RuMegafon]: MegafonIcon,
  [EOperatorName.RuYota]: EmptyIcon,
  [EOperatorName.RuVainahtelecom]: EmptyIcon,
  [EOperatorName.RuMotiv]: MotivIcon,
  [EOperatorName.RuKtelecom]: KTelecomIcon,
  [EOperatorName.RuKtktelecom]: KtkTelecomIcon,
  [EOperatorName.RuSbertelecom]: SberIcon,
  [EOperatorName.RuDefault]: RuFlagIcon,
};

enum EBlrOperatorName {
  BlrA1 = 'belarus-a1',
  BlrMts = 'belarus-mts',
  BlrLife = 'belarus-life',
  BlrDefault = 'belarus-default',
}

enum EChannelType {
  SMS = 'SMS',
  WHATSAPP = 'WHATSAPP',
  VIBER = 'VIBER',
  PUSH = 'PUSH',
  VK_NOTIFY = 'VK_NOTIFY',
  OK_NOTIFY = 'OK_NOTIFY',
  MAX_BOT = 'MAX_BOT',
  MAX = 'MAX',
  TELEGRAM = 'TELEGRAM',
}

const CHANNEL_TYPES = Object.values(EChannelType);

type TChannelType = keyof typeof EChannelType;

const CHANNEL_LABEL: Record<TChannelType, string> = {
  [EChannelType.SMS]: 'SMS',
  [EChannelType.VIBER]: 'Viber',
  [EChannelType.WHATSAPP]: 'WhatsApp',
  [EChannelType.PUSH]: 'Push',
  [EChannelType.VK_NOTIFY]: 'VK Notify',
  [EChannelType.OK_NOTIFY]: 'OK Notify',
  [EChannelType.MAX_BOT]: 'MAX Bot',
  [EChannelType.MAX]: 'MAX',
  [EChannelType.TELEGRAM]: 'Telegram',
};

enum EContentType {
  TEXT = 'TEXT',
  VCARD = 'VCARD',
  IMAGE = 'IMAGE',
  BUTTON = 'BUTTON',
  TYPING = 'TYPING',
  DOCUMENT = 'DOCUMENT',
  LOCATION = 'LOCATION',
  AUDIO = 'AUDIO',
  VIDEO = 'VIDEO',
  TYPING_END = 'TYPING_END',
  LIST_PICKER = 'LIST_PICKER',
  TIMEPICKER = 'TIMEPICKER',
  OAUTH = 'OAUTH',
  APPLE_PAY = 'APPLE_PAY',
  EXTENSION = 'EXTENSION',
  UNSUBSCRIBE = 'UNSUBSCRIBE',
  SUBSCRIBE = 'SUBSCRIBE',
  CONVERSATION_STARTER = 'CONVERSATION_STARTER',
  AUTHENTICATION = 'AUTHENTICATION',
  CONTACT = 'CONTACT',
  FLOW = 'FLOW',
  VOICE = 'VOICE',
  CALL_PERMISSION_REPLY = 'CALL_PERMISSION_REPLY',
}

type TContentType = keyof typeof EContentType;

const channelIconHash = {
  [EChannelType.SMS]: SmsCircleIcon,
  [EChannelType.VIBER]: ViberCircleIcon,
  [EChannelType.WHATSAPP]: WhatsAppCircleIcon,
  [EChannelType.PUSH]: PushCircleIcon,
  [EChannelType.VK_NOTIFY]: VkNotifyIcon,
  [EChannelType.OK_NOTIFY]: OkNotifyIcon,
  [EChannelType.MAX_BOT]: MaxBotCircleIcon,
  [EChannelType.MAX]: MaxCircleIcon,
  [EChannelType.TELEGRAM]: TelegramCircleIcon,
};

const channelBaseIconHash = {
  [EChannelType.SMS]: SmsIcon,
  [EChannelType.VIBER]: ViberIcon,
  [EChannelType.WHATSAPP]: WhatsAppIcon,
  [EChannelType.PUSH]: BellIcon,
  [EChannelType.VK_NOTIFY]: NotifyIcon,
  [EChannelType.OK_NOTIFY]: NotifyIcon,
  [EChannelType.MAX_BOT]: MaxBotCircleIcon,
  [EChannelType.MAX]: MaxCircleIcon,
  [EChannelType.TELEGRAM]: TelegramCircleIcon,
};

enum EAudienceGroupState {
  included = 'included',
  excluded = 'excluded',
}

type TAudienceGroupState = keyof typeof EAudienceGroupState;

enum EEvent {
  EDITOR_WITH_SELECTOR = 'EDITOR_WITH_SELECTOR',
  MENU_INTERACTION = 'MENU_INTERACTION',
  CANVAS_INTERACTION = 'CANVAS_INTERACTION',
  TRIGGER_MENU_DRAG_ITEM_SELECT = 'TRIGGER_MENU_DRAG_ITEM_SELECT',
}

enum ETimeUnit {
  hours = 'hours',
  minutes = 'minutes',
  seconds = 'seconds',
}

type TTimeUnit = keyof typeof ETimeUnit;

const TIME_UNITS = Object.values(ETimeUnit);

enum ESubjectStatus {
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  PENDING = 'PENDING',
  NOT_SENT = 'NOT_SENT',
}

type TSubject = {
  id: number;
  name: string;
  subject: string;
  type: TChannelType;
  locked: boolean;
  status: keyof typeof ESubjectStatus;
  testing: boolean;
  updatedAt: string;
  timeZone?: string;
  statusComment?: string;
  createdAt?: string;
  description?: string;
};

enum ETenantCode {
  TENANT_TEMPORARY_BLOCKED = 'tenant.temporary-blocked',
}

enum EDomain {
  // production
  RU = 'app.edna.ru',
  IO = 'app.edna.io',
  BY = 'app.edna.by',

  // development
  PERF = 'app-uat.edna.ru',
  DEV = 'edna.dte',
  RTM = 'app.edna.rtm.dte',
  TDS = 'app.tds.edna.dte',
  AQA = 'app.edna.aqa.dte',
}

const EN_DOMAIN_POSTFIX = '.io';
const BY_DOMAIN_POSTFIX = '.by';
const RU_DOMAIN_POSTFIX = '.ru';
const PROD_DOMAINS: string[] = [EDomain.RU, EDomain.IO, EDomain.BY];

const isEnDomain = () => window.location.hostname.endsWith(EN_DOMAIN_POSTFIX);
const isByDomain = () => window.location.hostname.endsWith(BY_DOMAIN_POSTFIX);
const isRuDomain = () => window.location.hostname.endsWith(RU_DOMAIN_POSTFIX);
const isProdDomain = () => PROD_DOMAINS.includes(window.location.hostname);
const isPreProdDomain = () => window.location.hostname === EDomain.PERF;
const isEnvDevelopment = process.env.NODE_ENV === 'development';

const getDomainByLanguage = (isRu: boolean) => {
  const domain = window.location.hostname;

  if (isRu) {
    return domain.replace(new RegExp(`${EN_DOMAIN_POSTFIX}$`), RU_DOMAIN_POSTFIX);
  }

  return domain.replace(new RegExp(`${RU_DOMAIN_POSTFIX}$`), EN_DOMAIN_POSTFIX);
};

enum ESubmitType {
  SAVE = 'save',
  REGISTER = 'register',
}

type TItemId = string | number;

type TFiltersProps<T = TAnyObject> = {
  filters: T;
  hasFilters: boolean;
  resetFilters: TEmptyFunction;
  toggleFilter: TEmptyFunction;
  setFilters: React.Dispatch<React.SetStateAction<T>>;
};

const DEFAULT_DEBOUNCE_DELAY = 500;

const MIN_CALENDAR_DATE = new Date(1900, 0, 1);
const MAX_CALENDAR_DATE = new Date(2050, 0, 1);
const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export {
  ESortDirection,
  ECurrency,
  ELanguage,
  ESubmitType,
  ETenantCode,
  ESubjectStatus,
  EEvent,
  TIME_UNITS,
  EContentType,
  isEnDomain,
  isByDomain,
  isRuDomain,
  isProdDomain,
  isPreProdDomain,
  getDomainByLanguage,
  isEnvDevelopment,
  RU_DOMAIN_POSTFIX,
  EN_DOMAIN_POSTFIX,
  CHANNEL_LABEL,
  CHANNEL_TYPES,
  EAudienceGroupState,
  EStorageKeys,
  EOperatorName,
  EBlrOperatorName,
  channelBaseIconHash,
  channelIconHash,
  EChannelType,
  DEFAULT_DEBOUNCE_DELAY,
  MAX_CALENDAR_DATE,
  MIN_CALENDAR_DATE,
  XLSX_MIME_TYPE,
  EDomain,
};

export type {
  TFiltersProps,
  TItemId,
  TContentType,
  TSubject,
  TLanguage,
  TTimeUnit,
  TAudienceGroupState,
  TChannelType,
};
